import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CateringVendor } from '../../../../core/models/catering.model';
import { LocaleService } from '../../../../core/services/locale.service';
import { isVendorOpen, nextOpenTime, formatNextOpen } from '../../../../core/utils/catering';
import { isVendorType, VendorType } from '../../../../core/constants/enums';

/**
 * Vendor card — UberEats-inspired design with hero banner, overlapping logo,
 * and key info (rating, delivery time, tags).
 */
@Component({
  selector: 'ct-vendor-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe],
  template: `
    <article class="vc" [class.closed]="!isOpen()" (click)="selected.emit(vendor())"
             (keydown.enter)="selected.emit(vendor())"
             (keydown.space)="$event.preventDefault(); selected.emit(vendor())"
             role="button" tabindex="0"
             [attr.aria-label]="vendorAriaLabel()"
             data-testid="vendor-card"
             [attr.data-vendor-id]="vendor()._id"
             [attr.data-vendor-name]="vendor().name">
      <!-- Hero banner -->
      <div class="vc__hero">
        @if (heroImage()) {
          <img [src]="heroImage()" [alt]="coverAlt()" loading="lazy"
               (error)="onHeroError($event)" />
        }
        <div class="vc__hero-gradient"></div>

        <!-- Open/Closed pill -->
        <span class="vc__status-pill" [class.open]="isOpen()">
          @if (isOpen()) { {{ 'catering.customer.vendor_card.open' | transloco }} } @else { {{ 'catering.customer.vendor_card.closed' | transloco }} }
        </span>

        @if (isVendorType(vendor().vendorType, VendorType.EMAIL_ONLY)) {
          <span class="vc__type-pill">{{ 'catering.customer.vendor_card.email_order' | transloco }}</span>
        }
      </div>

      <!-- Logo — overlaps the hero/body boundary -->
      <div class="vc__logo-wrap">
        @if (vendor().logo) {
          <img class="vc__logo" [src]="vendor().logo" [alt]="vendor().name"
               (error)="onLogoError($event)" />
        } @else {
          <span class="vc__logo vc__logo--letter">{{ vendor().name.charAt(0) }}</span>
        }
      </div>

      <!-- Body -->
      <div class="vc__body">
        <h3 class="vc__name">{{ vendor().name }}</h3>

        <div class="vc__info-row">
          @if (vendor().ratings; as r) {
            <span class="vc__rating">
              <span class="vc__star" aria-hidden="true">&#9733;</span> <span class="sr-only">{{ 'catering.customer.vendor_card.rating_sr' | transloco }}</span> {{ r.average.toFixed(1) }}
              <span class="vc__rating-count">({{ r.count }})</span>
            </span>
          }
          @if (isOpen() && vendor().estimatedDeliveryMinutes) {
            <span class="vc__dot-sep">&middot;</span>
            <span class="vc__eta">{{ etaLabel(vendor().estimatedDeliveryMinutes) }}</span>
          }
          @if (!isOpen() && nextOpen()) {
            <span class="vc__dot-sep">&middot;</span>
            <span class="vc__reopens">{{ opensLabel() }}</span>
          }
        </div>

        <div class="vc__tags">
          @for (tag of vendor().tags.slice(0, 4); track tag) {
            <span class="vc__tag">{{ tag }}</span>
          }
        </div>
      </div>
    </article>
  `,
  styles: [`
    :host { display: block; }

    .vc {
      border-radius: var(--radius);
      border: 1px solid var(--border-low);
      background: var(--surface-2);
      overflow: hidden;
      cursor: pointer;
      transition: border-color 0.2s ease, transform 0.15s ease;
      position: relative;

      &:hover {
        border-color: var(--border-med);
        transform: translateY(-2px);
      }

      &:active { transform: translateY(0); }

      &.closed { opacity: 0.55; }
    }

    /* ── Hero banner ───────────────── */

    .vc__hero {
      position: relative;
      height: 110px;
      background: linear-gradient(135deg, var(--surface-3) 0%, var(--surface-2) 100%);
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    .vc__hero-gradient {
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to bottom,
        transparent 40%,
        rgba(0, 0, 0, 0.55) 100%
      );
      pointer-events: none;
    }

    .vc__status-pill {
      position: absolute;
      top: 8px;
      left: 8px;
      font-size: 10px; /* type-scale floor */
      font-weight: 700;
      padding: 3px 8px;
      border-radius: var(--radius-pill);
      text-transform: uppercase;
      letter-spacing: 0.4px;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);

      /*
       * Translucent glass pills over a photo hero need a colour with alpha.
       * color-mix lets us derive an 85%-opacity tint from the --success token,
       * so the pill themes correctly in light/dark modes.
       */
      &.open {
        background: color-mix(in srgb, var(--success) 85%, transparent);
        color: var(--text-on-accent, #fff);
      }

      &:not(.open) {
        background: color-mix(in srgb, var(--surface-3) 70%, transparent);
        color: var(--text-secondary);
      }
    }

    .vc__type-pill {
      position: absolute;
      top: 8px;
      right: 8px;
      font-size: 10px; /* type-scale floor */
      font-weight: 600;
      padding: 2px 7px;
      border-radius: var(--radius-pill);
      border: 1px dashed rgba(255, 255, 255, 0.3);
      color: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
    }

    /* ── Logo ───────────────────────── */

    .vc__logo-wrap {
      position: relative;
      margin: -18px 0 0 10px;
      width: 36px;
      height: 36px;
      z-index: 2;
    }

    .vc__logo {
      width: 36px;
      height: 36px;
      border-radius: 10px; /* squircle */
      border: 2px solid var(--surface-2);
      background: #fff;
      object-fit: contain;
      padding: 3px;
      display: block;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);

      &--letter {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        font-size: 16px;
        font-weight: 700;
        color: var(--text-on-accent, #fff);
        background: var(--color-primary);
      }
    }

    /* ── Body ───────────────────────── */

    .vc__body {
      padding: 6px 10px 10px;
    }

    .vc__name {
      font-size: 14px; /* design flow-vendors: card name reads at 14px */
      font-weight: 600;
      color: var(--text);
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .vc__info-row {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 3px;
      font-size: 11px;
      color: var(--text-secondary);
    }

    .vc__star { color: var(--warning); }

    .vc__rating-count {
      color: var(--text-tertiary);
      font-size: 11px;
    }

    .vc__dot-sep { color: var(--text-tertiary); }

    .vc__eta { color: var(--text-secondary); }

    .vc__reopens {
      color: var(--text-tertiary);
      font-size: 10px;
    }

    .vc__tags {
      display: flex;
      flex-wrap: wrap;
      gap: 3px;
      margin-top: 6px;
    }

    .vc__tag {
      font-size: 10px; /* type-scale floor */
      padding: 2px 7px;
      border-radius: var(--radius-pill);
      background: var(--glass-tint);
      color: var(--text-tertiary);
      white-space: nowrap;
    }

    @media (prefers-reduced-motion: reduce) {
      .vc { transition: none; }
    }
  `],
})
export class VendorCardComponent {
  readonly vendor = input.required<CateringVendor>();
  readonly selected = output<CateringVendor>();
  protected readonly isVendorType = isVendorType;
  protected readonly VendorType = VendorType;
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected readonly isOpen = computed(() =>
    isVendorOpen(this.vendor().operatingHours),
  );

  protected readonly nextOpen = computed(() =>
    nextOpenTime(this.vendor().operatingHours),
  );

  /** The next-open moment as words, in the reader's own language. */
  protected readonly nextOpenLabel = computed(() => {
    this.locale.locale();
    return formatNextOpen(this.nextOpen(), (key, params) =>
      this.transloco.translate(key, params),
    );
  });

  protected readonly heroImage = computed(() => {
    const v = this.vendor();
    return v.coverImage || null;
  });

  protected readonly vendorAriaLabel = computed(() => {
    this.locale.locale();
    const name = this.vendor().name;
    return this.isOpen() ? name : this.transloco.translate('catering.customer.vendor_card.aria_closed', { name });
  });

  protected readonly coverAlt = computed(() => {
    this.locale.locale();
    return this.transloco.translate('catering.customer.vendor_card.cover_alt', { name: this.vendor().name });
  });

  protected etaLabel(minutes: number | undefined): string {
    this.locale.locale();
    return this.transloco.translate('catering.customer.vendor_card.eta', { minutes });
  }

  protected readonly opensLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('catering.customer.vendor_card.opens', {
      when: this.nextOpenLabel(),
    });
  });

  protected onHeroError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  protected onLogoError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }
}
